import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { ThemeInputFileSingle, FormCard, GrayColorButton, SecondColorButton } from './Controls';
import { apiURL } from './App';
import { authJson, authFetch } from './apiClient';

// 型定義
interface ImportForm { cybozuFile: File | null; }

// サイボウズ連携パネル
const AdminCybozuPanel: React.FC = () => {
  // フォーム（番組名などの追加入力が必要なら）
  const { control: importControl, reset: resetImport, setValue } = useForm<ImportForm>({ defaultValues: { cybozuFile: null } });
  // 選択ファイル（送信用に保持）
  const [ file, setFile ] = useState<File | null>(null);
  // 通知
  const [toast, setToast] = useState<{ open: boolean; msg: string; severity: 'success'|'error'|'info'|'warning' }>({ open: false, msg: '', severity: 'info' });
  const openToast = (msg: string, severity: typeof toast.severity = 'info') => setToast({ open: true, msg, severity });

  // インポート確認ダイアログ制御
  const [confirmOpen, setConfirmOpen] = useState(false);
  const onImport = () => {
    if (!file) { openToast('インポートするファイルを選択してください', 'warning'); return; }
    setConfirmOpen(true);
  };
  // 確認ダイアログ閉じる
  const closeConfirmDialog = () => setConfirmOpen(false);

  // インポート
  const handleImportConfirm = async () => {
    // ダイアログ閉じる
    closeConfirmDialog();

    // インポート処理
    try {
      // API 呼び出し
      const fd = new FormData();
      if (file) fd.append('file', file);
      const res = await authFetch(`${apiURL}/schedules/import`, { method: 'POST', body: fd, actionName: 'ImportCybozu' });
      if (res.ok) {
        // インポート成功
        openToast('インポートしました', 'success');
        // 選択解除 & フォームクリア
        setFile(null);
        resetImport();
      }
    }
    catch (e) {
      // インポートエラー
      console.error(e);
      openToast('インポートに失敗しました', 'error');
    }
  };

  // レンダリング
  return (
    <Box display="flex" gap={2}>
      {/* 左側: 番組追加 (35%) */}
      <Box sx={{ width: '35%' }}>
        <FormCard style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', alignSelf: 'flex-start', mb: 2 }}>データインポート</Typography>
          <Stack direction="column" spacing={2}>
            <ThemeInputFileSingle
              id="cybozuFile"
              label="サイボウズから出力されたデータを選択"
              placeholder="データをインポート"
              control={importControl}
              accept=".csv"
              onFileChange={(f: File | null) => { setFile(f); }}
            />
            <Box display="flex" gap={1} justifyContent="flex-end">
              <GrayColorButton onClick={() => { resetImport(); setFile(null); }}>クリア</GrayColorButton>
              <SecondColorButton onClick={onImport}>決定</SecondColorButton>
            </Box>
          </Stack>
        </FormCard>
      </Box>
      {/* 右側: 番組管理 (65%) */}
      <Box sx={{ width: '65%' }}>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={(e, r) => { if (r === 'clickaway') return; setToast(t => ({ ...t, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
      <Dialog open={confirmOpen} onClose={closeConfirmDialog}>
        <DialogTitle>インポート確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2">選択中のファイルをインポートします。よろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} variant="contained" size="small" sx={{ color: 'white', backgroundColor: 'gray', '&:hover': { backgroundColor: '#555' } }} autoFocus>キャンセル</Button>
          <Button onClick={handleImportConfirm} variant="contained" color="warning" size="small">インポート</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCybozuPanel;
